import * as studentApi from '@/api/student';

/**
 * 测试类型文本映射
 */
const testTypeText = (type) => {
  const textMap = {
    en_to_cn: '英译中',
    cn_to_en: '中译英',
    listening: '听力测试',
    spelling: '拼写测试',
    daily: '日常测试',
    weekly: '周测',
    monthly: '月测',
    final: '期末测试',
  };
  return textMap[type] || '未知类型';
};

/**
 * 难度文本映射
 */
const difficultyText = (difficulty) => {
  const textMap = {
    easy: '简单',
    medium: '中等',
    hard: '困难',
  };
  return textMap[difficulty] || '未知难度';
};

const state = {
  studentList: [],
  currentStudent: null,
};

const getters = {
  studentList: (state) => state.studentList,
  currentStudent: (state) => state.currentStudent,
};

const mutations = {
  SET_STUDENT_LIST(state, list) {
    state.studentList = list;
  },
  SET_CURRENT_STUDENT(state, student) {
    state.currentStudent = student;
  },
  ADD_STUDENT(state, student) {
    state.studentList.unshift(student);
  },
  UPDATE_STUDENT(state, student) {
    const index = state.studentList.findIndex((item) => item.id === student.id);
    if (index !== -1) {
      state.studentList.splice(index, 1, student);
    }
  },
  DELETE_STUDENT(state, studentId) {
    const index = state.studentList.findIndex((item) => item.id === studentId);
    if (index !== -1) {
      state.studentList.splice(index, 1);
    }
  },
};

const actions = {
  /**
   * 获取学员列表
   */
  async fetchStudentList({ commit }, params) {
    try {
      // 优先尝试真实API
      const response = await studentApi.getStudentList(params);
      console.log("获取学员列表:", response);
      
      // 处理不同的响应格式
      let list = [];
      if (Array.isArray(response)) {
        list = response;
      } else if (response && Array.isArray(response.data)) {
        list = response.data;
      } else if (response && Array.isArray(response.list)) {
        list = response.list;
      } else if (response && response.data && Array.isArray(response.data.list)) {
        list = response.data.list;
      }
      
      commit('SET_STUDENT_LIST', list);
      return { list, total: response.total };
    } catch (error) {
      console.error('API获取学员列表失败:', error);
      throw error;
    }
  },

  /**
   * 获取学员详情
   */
  async fetchStudentDetail({ commit }, studentId) {
    try {
      const response = await studentApi.getStudentDetail(studentId);
      
      // 处理嵌套的响应格式
      let studentData = response;
      let statsData = null;
      
      // 如果数据在 data.student 中
      if (response?.data?.student) {
        studentData = {
          ...response.data.student,
          // 将 nickname 映射到 real_name
          real_name: response.data.student.nickname || response.data.student.real_name,
          // 将 createtime 时间戳转换为可读格式(可选,如果需要)
          createTime: response.data.student.createtime 
            ? new Date(response.data.student.createtime * 1000).toLocaleString('zh-CN')
            : '-',
        };
        
        // 提取 stats 数据
        if (response.data.stats) {
          statsData = response.data.stats;
          studentData.stats = statsData;
        }
      } else if (response?.student) {
        // 如果数据在 student 中
        studentData = {
          ...response.student,
          real_name: response.student.nickname || response.student.real_name,
          createTime: response.student.createtime 
            ? new Date(response.student.createtime * 1000).toLocaleString('zh-CN')
            : '-',
        };
        
        // 提取 stats 数据
        if (response.stats) {
          statsData = response.stats;
          studentData.stats = statsData;
        }
      }
      
      commit('SET_CURRENT_STUDENT', studentData);
      return studentData;
    } catch (error) {
      console.error('API获取学员详情失败:', error);
      throw error;
    }
  },

  /**
   * 添加学员
   */
  async addStudent({ commit }, data) {
    try {
      const response = await studentApi.addStudent(data);
      
      // 处理响应数据
      let studentData = response;
      if (response && typeof response === 'object') {
        // 如果返回的是包含学员信息的对象
        studentData = {
          id: response.id || Date.now(),
          name: data.name,
          phone: data.phone,
          grade: data.grade,
          gender: data.gender,
          parentName: data.parentName,
          parentPhone: data.parentPhone,
          status: data.status || 'active',
          totalWords: 0,
          studyDays: 0,
          createTime: new Date().toLocaleString('zh-CN'),
          ...response,
        };
      }
      
      commit('ADD_STUDENT', studentData);
      return studentData;
    } catch (error) {
      console.error('API添加学员失败:', error);
      throw error;
    }
  },

  /**
   * 更新学员
   */
  async updateStudent({ commit }, { id, ...data }) {
    try {
      const response = await studentApi.updateStudent(id, data);
      commit('UPDATE_STUDENT', response);
      return response;
    } catch (error) {
      console.error('API更新学员失败:', error);
      throw error;
    }
  },

  /**
   * 删除学员
   */
  async deleteStudent({ commit }, studentId) {
    try {
      await studentApi.deleteStudent(studentId);
      commit('DELETE_STUDENT', studentId);
    } catch (error) {
      console.error('API删除学员失败:', error);
      throw error;
    }
  },

  /**
   * 更新学员设置
   */
  async updateStudentSettings(context, { studentId, settings }) {
    try {
      return await studentApi.updateStudentSettings(studentId, settings);
    } catch (error) {
      console.error('API更新学员设置失败:', error);
      throw error;
    }
  },

  /**
   * 获取学员课程列表 (使用getStudentDetail接口的courses字段)
   */
  async fetchStudentCourses(context, studentId) {
    try {
      // 调用学员详情接口获取课程列表
      const response = await studentApi.getStudentDetail(studentId);

      // 从响应中提取courses数组
      let courses = [];
      if (response?.data?.courses) {
        courses = response.data.courses;
      } else if (response?.courses) {
        courses = response.courses;
      } else if (Array.isArray(response)) {
        courses = response;
      }
      
      // 将后端返回的课程格式转换为前端需要的格式
      const formattedCourses = courses.map(course => {
        // 计算状态
        let status = 'active';
        if (course.is_completed === 1 || course.is_completed === true) {
          status = 'completed';
        } else if (course.is_active === 0 || course.is_active === false) {
          status = 'inactive';
        }
        
        return {
          id: course.id,
          courseId: course.stage_id || course.id, // 课程阶段ID
          courseName: course.name || course.stage_name || '未知课程',
          wordCount: course.total_words || 0,
          progress: parseFloat(course.current_progress) || 0,
          learnedWords: course.mastered_words || 0,
          purchaseDate: course.created_at ? course.created_at.split(' ')[0] : '-',
          expireDate: '-', // 如果有过期时间字段,从这里映射
          status: status,
          lastStudyTime: course.last_study_time,
          studyDays: course.study_days || 0,
          totalStudyTime: course.total_study_time || 0,
          completionRate: parseFloat(course.completion_rate) || 0,
        };
      });
      
      return formattedCourses;
    } catch (error) {
      console.error('API获取学员课程失败:', error);
      throw error;
    }
  },

  /**
   * 移除学员课程
   */
  async removeStudentCourse(context, { studentId, courseId }) {
    try {
      return await studentApi.removeStudentCourse(studentId, courseId);
    } catch (error) {
      console.error('API移除学员课程失败:', error);
      throw error;
    }
  },

  /**
   * 获取学习数据和最近单词 (优化版:一次接口调用获取两部分数据)
   */
  async fetchLearningDataWithWords(context, params) {
    try {
      const response = await studentApi.getStudentDetail(params.studentId);
      
      // 从getStudentDetail响应中提取stats数据
      let stats = null;
      if (response?.data?.stats) {
        stats = response.data.stats;
      } else if (response?.stats) {
        stats = response.stats;
      }
      
      if (stats) {
        // 格式化学习数据
        const learningData = {
          totalWords: stats.totalWords || 0,
          masteredWords: stats.masteredWords || 0,
          studyDays: stats.studyDays || 0,
          consecutiveDays: stats.consecutiveDays || 0,
          todayWords: stats.todayWords || 0,
          reviewWords: stats.reviewingWords || 0,
          newWords: stats.pendingWords || 0,
        };
        
        // 格式化最近单词
        const recentWords = (stats.recentWords || []).map(word => ({
          word: word.word,
          translation: word.meaning || word.translation,
          reviewCount: word.reviewCount || 0,
          masteryLevel: word.masteryLevel || 0,
          lastReviewTime: word.lastReviewed || word.lastReviewTime || '-',
        }));
        
        return {
          learningData,
          recentWords,
        };
      }
      
      // 如果没有数据,返回空结构
      return {
        learningData: {
          totalWords: 0,
          masteredWords: 0,
          reviewWords: 0,
          newWords: 0,
          studyDays: 0,
          consecutiveDays: 0,
          todayWords: 0,
        },
        recentWords: [],
      };
    } catch (error) {
      console.error('API获取学习数据失败:', error);
      throw error;
    }
  },

  /**
   * 获取每日统计 (使用getStudentDailyData接口)
   */
  async fetchDailyStats(context, params) {
    try {
      const response = await studentApi.getDailyStats(params);
      
      // 提取数据数组
      let dailyData = [];
      if (Array.isArray(response)) {
        dailyData = response;
      } else if (response?.data && Array.isArray(response.data)) {
        dailyData = response.data;
      }
      
      // 计算统计数据
      if (dailyData.length > 0) {
        // 计算平均每日单词数
        const totalNewWords = dailyData.reduce((sum, item) => sum + (item.new_words_count || 0), 0);
        const avgDaily = dailyData.length > 0 ? Math.round(totalNewWords / dailyData.length) : 0;
        
        // 获取连续天数 (使用最新数据的streak_days)
        const continuousDays = dailyData[0].streak_days || 0;
        
        // 计算平均学习时长 (分钟)
        const totalTime = dailyData.reduce((sum, item) => sum + (item.study_time || 0), 0);
        const avgDuration = dailyData.length > 0 ? Math.round(totalTime / dailyData.length) : 0;
        
        // 计算目标完成率
        const totalGoals = dailyData.reduce((sum, item) => sum + (item.study_goals || 0), 0);
        const totalCompleted = dailyData.reduce((sum, item) => sum + (item.goals_completed || 0), 0);
        const completionRate = totalGoals > 0 ? Math.round((totalCompleted / totalGoals) * 100) : 0;
        
        return {
          avgDaily,
          continuousDays,
          avgDuration,
          completionRate,
        };
      }
      
      return {
        avgDaily: 0,
        continuousDays: 0,
        avgDuration: 0,
        completionRate: 0,
      };
    } catch (error) {
      console.error('API获取每日统计失败:', error);
      throw error;
    }
  },

  /**
   * 获取每日记录 (使用getStudentDailyData接口)
   */
  async fetchDailyRecords(context, params) {
    try {
      const response = await studentApi.getDailyRecords(params);
      
      // 提取数据数组
      let dailyData = [];
      if (Array.isArray(response)) {
        dailyData = response;
      } else if (response?.data && Array.isArray(response.data)) {
        dailyData = response.data;
      }
      
      // 将后端数据转换为前端需要的格式
      const records = dailyData.map(item => ({
        date: item.date,
        newWords: item.new_words_count || 0,
        reviewWords: item.reviewed_count || 0,
        totalWords: item.total_words || (item.new_words_count || 0) + (item.reviewed_count || 0),
        duration: item.study_time || 0, // 学习时长(分钟)
        targetCompletion: item.study_goals > 0 
          ? Math.round((item.goals_completed / item.study_goals) * 100) 
          : 0,
        accuracy: parseFloat(item.accuracy) || 0,
        notes: item.study_notes || '',
        earnedCoins: item.earned_coins || 0,
        streakDays: item.streak_days || 0,
        correctWords: item.correct_words || 0,
        wrongWords: item.wrong_words || 0,
      }));
      
      return records;
    } catch (error) {
      console.error('API获取每日记录失败:', error);
      throw error;
    }
  },

  /**
   * 获取完整时间数据 (优化版:一次接口调用获取所有时间相关数据)
   * 包括: 时间统计、周数据、月数据、时段分布
   */
  async fetchAllTimeData(context, studentId) {
    try {
      // 并行请求两个接口
      const [learningTimeResponse, dailyDataResponse] = await Promise.all([
        studentApi.getStudentLearningTime(studentId), // 获取时间统计+时段分布
        studentApi.getStudentDailyData({ studentId, days: 30 }), // 获取30天每日数据
      ]);
      
      // 处理时间统计和时段数据
      let timeStats = {
        totalHours: 0,
        weekHours: 0,
        monthHours: 0,
      };
      let periodData = {
        morning: 0,
        afternoon: 0,
        evening: 0,
        night: 0,
      };
      
      console.log('学习时间响应:', learningTimeResponse);
      if (learningTimeResponse) {
        const data = learningTimeResponse;
        timeStats = {
          totalHours: data.totalHours || 0,
          weekHours: data.weekHours || 0,
          monthHours: data.monthHours || 0,
        };
        periodData = {
          morning: data.morningMinutes || 0,
          afternoon: data.afternoonMinutes || 0,
          evening: data.eveningMinutes || 0,
          night: data.nightMinutes || 0,
        };
      }
      
      // 处理每日数据
      let dailyData = [];
      if (Array.isArray(dailyDataResponse)) {
        dailyData = dailyDataResponse;
      } else if (dailyDataResponse?.data && Array.isArray(dailyDataResponse.data)) {
        dailyData = dailyDataResponse.data;
      }
      
      // 提取周数据 (最近7天)
      const weekData = dailyData.slice(0, 7).reverse().map(item => item.study_time || 0);
      
      // 提取月数据 (最近30天)
      const monthData = dailyData.slice(0, 30).reverse().map(item => ({
        date: new Date(item.date).getDate() + '日',
        duration: item.study_time || 0,
      }));
      
      return {
        timeStats,
        weekData,
        monthData,
        periodData,
      };
    } catch (error) {
      console.error('API获取时间数据失败:', error);
      throw error;
    }
  },

  /**
   * 获取测试统计
   */
  async fetchTestStats(context, params) {
    try {
      const response = await studentApi.getTestStats(params);
      
      // 处理API返回的数据格式
      let statsData = response;
      if (response?.data) {
        statsData = response.data;
      }
      
      // 转换为前端需要的格式
      return {
        totalTests: statsData.totalTests || 0,
        avgScore: Math.round(statsData.avgAccuracy || 0), // avgAccuracy -> avgScore
        highestScore: Math.round(statsData.maxAccuracy || 0), // maxAccuracy -> highestScore
        passRate: statsData.passRate || 0,
        lastTest: statsData.lastTest,
        typeStats: statsData.typeStats || [],
        difficultyStats: statsData.difficultyStats || [],
        scoreTrend: statsData.scoreTrend || [],
      };
    } catch (error) {
      console.error('API获取测试统计失败:', error);
      throw error;
    }
  },

  /**
   * 获取测试记录 (使用getTestRecordList接口)
   */
  async fetchTestRecords(context, params) {
    try {
      // 调用测试记录列表接口
      const response = await studentApi.getTestRecords(params);
      
      // 从响应中提取list数组
      let recordList = [];
      if (response?.data?.list) {
        recordList = response.data.list;
      } else if (response?.list) {
        recordList = response.list;
      } else if (Array.isArray(response)) {
        recordList = response;
      }
      
      // 将后端返回的数据转换为前端需要的格式
      const testRecords = recordList.map(record => ({
        id: record.id,
        testName: `${testTypeText(record.test_type)} - ${difficultyText(record.difficulty)}`,
        testType: record.test_type,
        totalQuestions: record.total_questions || 0,
        correctCount: record.correct_answers || 0,
        score: Math.round(parseFloat(record.accuracy) || 0),
        duration: Math.round(record.total_time || 0), // 秒转为分钟可能需要除以60,这里保持秒
        testDate: record.created_at || '-',
        difficulty: record.difficulty,
        username: record.username,
        nickname: record.nickname,
        avatar: record.avatar,
      }));
      
      return testRecords;
    } catch (error) {
      console.error('API获取测试记录失败:', error);
      throw error;
    }
  },

  /**
   * 删除测试记录
   */
  async deleteTestRecord(context, testId) {
    try {
      return await studentApi.deleteTestRecord(testId);
    } catch (error) {
      console.error('API删除测试记录失败:', error);
      throw error;
    }
  },

  /**
   * 获取可用课程列表
   */
  async fetchAvailableCourses() {
    try {
      const result = await studentApi.fetchAvailableCourses();
      // 处理不同的响应格式
      let courseList = [];
      if (Array.isArray(result)) {
        courseList = result;
      } else if (result && Array.isArray(result.data)) {
        courseList = result.data;
      } else if (result && Array.isArray(result.list)) {
        courseList = result.list;
      } else if (result && result.data && Array.isArray(result.data.list)) {
        courseList = result.data.list;
      }
      
      return courseList;
    } catch (error) {
      console.error('API获取可用课程失败:', error);
      throw error;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
